import React from 'react'
import CommunityPlaylistRowHeader from '../CommunityPlaylistRowHeader/index'
import {
  ResponsivePlaylistDisplay,
  PlaylistItemCarouselSkeleton,
  type EditToolbarLabels,
  type CarouselItem,
} from '../CommunityProfileCarousels/index'
import {
  PlaylistRowContext,
  useRowContext,
  type PlaylistRowContextValue,
} from './PlaylistRowContext'

/**
 * `<PlaylistRow.*>` compound component.
 *
 * Four members:
 *   - `Provider` — wraps subtree in row context.
 *   - `Frame`    — outer element. Caller picks parent-scoped className.
 *   - `Header`   — facade over the OUTER `CommunityPlaylistRowHeader`
 *                  (never `Base`). The outer wrapper is what makes the
 *                  mobile breakpoint work — it calls `useIsMobile()`
 *                  internally and maps `statusTag` to a below-title
 *                  row at narrow widths. NO new `useIsMobile()` here.
 *   - `Body`     — owns the skeleton / empty / carousel decision tree.
 *                  Renders `<ResponsivePlaylistDisplay>` exactly once;
 *                  it handles its own mobile (horizontal scroll) vs
 *                  desktop (grid) split internally.
 *
 * Deliberately no `Header.StatusTag` / `Header.Actions` /
 * `Header.EmptyState` / `Body.Skeleton` sub-components. Slot content
 * is passed as props on `<Header>` and forwarded verbatim to the
 * outer wrapper — adding sub-components would just duplicate the
 * wrapper's slot API.
 */

interface FrameProps {
  /** Required — caller picks parent-scoped class. The compound deliberately has no
   * global row block today (a CSS unify ticket would be a separate piece of work). */
  className: string
  children: React.ReactNode
  /** Following tab uses scroll-to-row refs. */
  innerRef?: React.Ref<HTMLDivElement>
  /** Following + Discover capture for PostHog tracking. */
  onClickCapture?: React.MouseEventHandler<HTMLDivElement>
  /** e.g. `role="presentation"` for click-capture rows. */
  role?: string
  /** Optional DOM id — system rows use `system-playlist-<id>` for scroll-into-view. */
  id?: string
  /** Optional inline style — used by toast collapse animation. */
  style?: React.CSSProperties
}

function Frame ({
  className, children, innerRef, onClickCapture, role, id, style,
}: FrameProps) {
  return (
    <div
      ref={innerRef}
      className={className}
      onClickCapture={onClickCapture}
      role={role}
      id={id}
      style={style}
    >
      {children}
    </div>
  )
}

interface HeaderProps {
  /** Pass-through to outer wrapper slots. */
  authorDisplay?: string | null
  authorUid?: string | null
  followerCount?: number
  nameAction?: React.ReactNode
  statusTag?: React.ReactNode
  shareButton?: React.ReactNode
  customStats?: React.ReactNode
  badge?: { label: string; variant: 'progress' | 'topic' } | null
  /** Edit-mode rename input wiring. */
  editingName?: string
  onNameChange?: ((value: string) => void) | null
}

function Header ({
  authorDisplay = null,
  authorUid = null,
  followerCount = 0,
  nameAction,
  statusTag,
  shareButton,
  customStats,
  badge = null,
  editingName,
  onNameChange = null,
}: HeaderProps) {
  const ctx = useRowContext()
  // The default stats row's three slots (episodes, duration, author) appear
  // only after `useRowHydration` resolves — that causes a ~20px height jump
  // and shifts every sibling row below. While hydration is in flight we
  // render invisible placeholder slots so the line-box stays stable. When
  // the consumer supplies `customStats` (e.g. MyPlaylistRow's explicit
  // zero-stats node, Currently Watching's series-count node) they own the
  // slot and the placeholder is correctly skipped.
  const statsPending = ctx.isHydrating && customStats === undefined
  return (
    <CommunityPlaylistRowHeader
      name={ctx.playlistName}
      badge={badge}
      episodeCount={ctx.expectedItemCount}
      totalDuration={ctx.totalDuration}
      authorDisplay={authorDisplay}
      authorUid={authorUid}
      followerCount={followerCount}
      nameAction={nameAction}
      editMode={ctx.isEditing}
      editingName={editingName}
      onNameChange={onNameChange}
      statusTag={statusTag}
      shareButton={shareButton}
      customStats={customStats}
      statsPending={statsPending}
    />
  )
}

interface BodyProps {
  seeMoreLabel: string
  showLessLabel?: string
  /** Default 4 — must equal skeleton count below to keep the row stable on hydration. */
  initialCount?: number
  hideToggle?: boolean
  editToolbarLabels?: EditToolbarLabels
  onRemoveItem?: (item: CarouselItem) => void
  onReorderItem?: (fromIndex: number, toIndex: number) => void
  /** Optional node rendered when items.length === 0 and not skeleton-loading. */
  emptyState?: React.ReactNode
  /**
   * Fallback skeleton count for variants without a shell (Currently Watching,
   * Watch History): when `isHydrating && items.length === 0` and
   * `expectedItemCount === 0`, this kicks the row into the skeleton branch
   * instead of the empty-state branch. Shell-aware variants (Watch Later,
   * Favorites, custom rows) MUST NOT pass this — `expectedItemCount` from
   * the shell is the source of truth, and an empty shell should render the
   * empty-state immediately with no skeleton flash.
   */
  skeletonCount?: number
  /**
   * Opt in to the series-label corner-badge placeholder in the skeleton
   * branch.  Default `false` — only Currently Watching's
   * `SystemPlaylistExternalRow` passes `true` today.  Named with the
   * `Skeleton…` qualifier because `Body` also renders the real
   * carousel; the prop only affects the loading state.
   */
  showSkeletonSeriesLabel?: boolean
}

function Body ({
  seeMoreLabel,
  showLessLabel,
  initialCount = 4,
  hideToggle,
  editToolbarLabels,
  onRemoveItem,
  onReorderItem,
  emptyState,
  skeletonCount,
  showSkeletonSeriesLabel = false,
}: BodyProps) {
  const ctx = useRowContext()
  const {
    items, isHydrating, expectedItemCount, isEditing,
  } = ctx

  // Skeleton path: shell says we have content, hydration hasn't produced
  // it yet. Distinct from genuinely empty so the empty-CTA branch doesn't
  // flash during the hydration window. Skeleton count equals initialCount
  // so the row's height matches the eventual carousel — no pop on resolve.
  //
  // External rows (Currently Watching / Watch History) don't have a shell,
  // so `expectedItemCount` is 0 until items arrive. The `skeletonCount`
  // fallback lets those variants opt in to the skeleton branch without
  // lying about the eventual item count. Shell rows never pass
  // `skeletonCount`, so their empty-shell behavior is unchanged.
  if (isHydrating && items.length === 0) {
    if (expectedItemCount > 0) {
      const count = Math.min(expectedItemCount, initialCount)
      return (
        <PlaylistItemCarouselSkeleton
          count={count}
          showSeriesLabel={showSkeletonSeriesLabel}
        />
      )
    }
    if (skeletonCount && skeletonCount > 0) {
      const count = Math.min(skeletonCount, initialCount)
      return (
        <PlaylistItemCarouselSkeleton
          count={count}
          showSeriesLabel={showSkeletonSeriesLabel}
        />
      )
    }
  }

  if (items.length === 0) {
    return emptyState ?? null
  }

  return (
    <ResponsivePlaylistDisplay
      items={items as CarouselItem[]}
      initialCount={initialCount}
      seeMoreLabel={seeMoreLabel}
      showLessLabel={showLessLabel}
      editMode={isEditing}
      onRemoveItem={onRemoveItem}
      onReorderItem={onReorderItem}
      editToolbarLabels={editToolbarLabels}
      hideToggle={hideToggle}
    />
  )
}

interface ProviderProps {
  value: PlaylistRowContextValue
  children: React.ReactNode
}

function Provider ({ value, children }: ProviderProps) {
  return (
    <PlaylistRowContext.Provider value={value}>
      {children}
    </PlaylistRowContext.Provider>
  )
}

const PlaylistRow = {
  Provider,
  Frame,
  Header,
  Body,
}

export default PlaylistRow
export type {
  FrameProps as PlaylistRowFrameProps,
  HeaderProps as PlaylistRowHeaderProps,
  BodyProps as PlaylistRowBodyProps,
}
